import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddCampaignRecipientsInput,
  AddCampaignRecipientsResult,
  Campaign,
  CampaignDetail,
  CollectionMeta,
  CreateCampaignInput,
  ListCampaignsQuery,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['campaigns'] as const;

export interface CampaignPage {
  items: Campaign[];
  meta: CollectionMeta;
}

function toQuery(q: ListCampaignsQuery): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize, status: q.status };
}

export async function listCampaigns(q: ListCampaignsQuery = {}): Promise<CampaignPage> {
  const res = await apiClient.getCollection<Campaign>('/campaigns', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}

export const getCampaign = (id: string) => apiClient.get<CampaignDetail>(`/campaigns/${id}`);
export const createCampaign = (input: CreateCampaignInput) =>
  apiClient.post<Campaign>('/campaigns', input);
export const addCampaignRecipients = (id: string, input: AddCampaignRecipientsInput) =>
  apiClient.post<AddCampaignRecipientsResult>(`/campaigns/${id}/recipients`, input);
export const startCampaign = (id: string) => apiClient.post<Campaign>(`/campaigns/${id}/start`);
export const cancelCampaign = (id: string) => apiClient.post<Campaign>(`/campaigns/${id}/cancel`);
export const pauseCampaign = (id: string) => apiClient.post<Campaign>(`/campaigns/${id}/pause`);
export const resumeCampaign = (id: string) => apiClient.post<Campaign>(`/campaigns/${id}/resume`);

export function useCampaigns(query: ListCampaignsQuery = {}) {
  return useQuery<CampaignPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listCampaigns(query),
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery<CampaignDetail, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getCampaign(id!),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data && ['QUEUED', 'SENDING'].includes(query.state.data.status) ? 4000 : false,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiClientError, CreateCampaignInput>({
    mutationFn: createCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddCampaignRecipients(id: string) {
  const qc = useQueryClient();
  return useMutation<AddCampaignRecipientsResult, ApiClientError, AddCampaignRecipientsInput>({
    mutationFn: (input) => addCampaignRecipients(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] }),
  });
}

export function useStartCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiClientError, void>({
    mutationFn: () => startCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useCancelCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiClientError, void>({
    mutationFn: () => cancelCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function usePauseCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiClientError, void>({
    mutationFn: () => pauseCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResumeCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiClientError, void>({
    mutationFn: () => resumeCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
